import { useQuery } from "@apollo/client";
import { Autocomplete, TextField } from "@mui/material";
import { gql } from "generated-graphql";
import { useEffect, useState } from "react";

const ORGANIZATIONS_QUERY = gql(`
  query EmailIntakeOrganizations($search: String, $pageSize: Int) {
    tenants(search: $search, page: 0, pageSize: $pageSize) {
      items {
        id
        name
      }
    }
  }
`);

export type OrganizationOption = { id: string; name: string };

/**
 * Which organization an allowlist entry belongs to.
 *
 * Required, and deliberately the first field in the dialog. On a per-customer page the
 * organization was fixed by the page itself; here it is not, and an allowlist write
 * decides whose documents a vendor's mail lands in — so getting it wrong is not a
 * cosmetic mistake, and there is nothing else on screen to catch it.
 */
export function OrganizationPicker({
  value,
  onChange,
  disabled,
  helperText,
}: {
  value: OrganizationOption | null;
  onChange: (organization: OrganizationOption | null) => void;
  disabled?: boolean;
  helperText?: string;
}) {
  // Controlled input seeded from the current selection, so editing an entry shows its
  // organization rather than a blank field. The dialog is keyed per row, so this
  // remounts and reseeds on open.
  const [inputValue, setInputValue] = useState(value?.name ?? "");
  // What was typed, kept apart from what the field displays: choosing an option
  // rewrites the display to that name, which is not a term worth looking up again.
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(typed), 400);
    return () => clearTimeout(timer);
  }, [typed]);

  const { data, loading } = useQuery(ORGANIZATIONS_QUERY, {
    variables: { search: search || null, pageSize: 20 },
    fetchPolicy: "cache-and-network",
  });

  const options: OrganizationOption[] = data?.tenants?.items ?? [];
  // Keep the current selection reachable when it falls outside the latest page.
  const withValue =
    value && !options.some((option) => option.id === value.id) ? [value, ...options] : options;

  return (
    <Autocomplete
      options={withValue}
      value={value}
      loading={loading}
      disabled={disabled}
      onChange={(_event, next) => onChange(next)}
      inputValue={inputValue}
      onInputChange={(_event, next, reason) => {
        setInputValue(next);
        if (reason === "input") setTyped(next);
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(all) => all}
      renderInput={(params) => (
        <TextField {...params} label="Organization" required helperText={helperText} />
      )}
    />
  );
}
